"use server";

import { randomUUID } from "node:crypto";
import { cancelWorkForOrder } from "./cancel-work";
import { z } from "zod";
import { CONTENT_SCHEMAS, describeValidation, essentialSectionError } from "./content-schemas";
import { headers } from "next/headers";
import { admin, authClient, currentUser } from "./db";
import { releaseDomain } from "./domain-locks";
import { isEmailBlocked } from "./blocked";
import { checkFormStamp } from "./contact-guard";
import { guardrail } from "./ai";
import { logEvent } from "./events";
import { sendOrderCancelled } from "./email";
import { newLaunchSlug, demoHost } from "./slug";
import { settings } from "./settings";
import { otpCounts, otpVerdict, devMailBlocked, saveGuardrailCount, saveVerdict } from "./rate-limit";
import { isReviewer } from "./reviewer";
import { revalidateLaunch, revalidateDomain, revalidateLive } from "./site";
import { demoSlotRefusal } from "./orders";
import { whyNotSubdomain, normaliseSubdomain } from "./subdomain-name";
import { whyNotStaticHtml, extractHtmlTitle } from "./static-html";
import { extractInvitationContent, extractBannerContent } from "./ai";
import { isCategory } from "./support";

// ─────────────────────────────────────────────────────────────────────────────
// lib/actions.ts — ALL customer mutations, as server actions (CLAUDE.md: every
// write is a server action via the service role; clients write nothing). Each
// action zod-validates input, recomputes quota/state from the DB (never trusts
// the client), runs the Haiku guardrail on every content save, and logs to
// events. Actions return serializable results — they never throw to the client.
// ─────────────────────────────────────────────────────────────────────────────

export type ActionResult<T = unknown> =
  | { ok: true; data?: T }
  /** `fields` names the exact inputs that failed, as dotted schema paths, so
   *  the form can mark the boxes the message is talking about. Optional: a
   *  refusal that is not about a field simply does not carry it. */
  | { ok: false; error: string; fields?: string[] };

const APEX = process.env.NEXT_PUBLIC_APEX ?? "makemysitetoday.com";
const CONTENT_CAP_BYTES = 50_000;

/**
 * If `url` is an inline data: URL (freshly uploaded), decode it, enforce
 * maxBytes, store it in the public `photos` bucket, and return the public
 * URL. http(s) URLs pass through unchanged.
 */
async function persistPhotoUrl(
  url: string,
  userId: string,
  maxBytes: number,
): Promise<string> {
  if (!url.startsWith("data:")) return url;
  const m = url.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!m) return "";
  const media = m[1];
  const buf = Buffer.from(m[2], "base64");
  if (buf.byteLength > maxBytes) throw new Error("PHOTO_TOO_LARGE");
  const ext = (media.split("/")[1] || "webp").replace(/\+.*$/, "");
  const path = `${userId}/${randomUUID()}.${ext}`;
  const { error } = await admin()
    .storage.from("photos")
    .upload(path, buf, { contentType: media, upsert: true });
  if (error) throw new Error("PHOTO_UPLOAD_FAILED");
  const { data } = admin().storage.from("photos").getPublicUrl(path);
  return data.publicUrl;
}

async function persistPhotoField(
  content: Record<string, unknown>,
  userId: string,
  field: string,
  maxBytes: number,
): Promise<Record<string, unknown>> {
  const url = content[field];
  if (typeof url !== "string" || !url.startsWith("data:")) return content;
  return { ...content, [field]: await persistPhotoUrl(url, userId, maxBytes) };
}

/** The one customer hero photo — up to the 500KB server cap (§3.4). */
async function persistHeroPhoto<T extends { heroPhoto?: string }>(
  content: T,
  userId: string,
): Promise<T> {
  return (await persistPhotoField(content, userId, "heroPhoto", 500_000)) as T;
}

/** The business logo (shop-services) — a smaller 200KB server cap. */
async function persistLogo<T extends { logo?: string }>(
  content: T,
  userId: string,
): Promise<T> {
  return (await persistPhotoField(content, userId, "logo", 200_000)) as T;
}

/** The work gallery (portfolio and others) — same 500KB-per-photo cap as the
 *  hero photo, applied to every data: URL in the array. */
async function persistGallery<T extends { gallery?: string[] }>(
  content: T,
  userId: string,
): Promise<T> {
  if (!content.gallery || content.gallery.length === 0) return content;
  const gallery = await Promise.all(
    content.gallery.map((url) => persistPhotoUrl(url, userId, 500_000)),
  );
  return { ...content, gallery: gallery.filter((u) => u.length > 0) };
}

function photoError(err: unknown): string {
  return err instanceof Error && err.message === "PHOTO_TOO_LARGE"
    ? "That photo is too large — please use a smaller image."
    : "Couldn't upload your photo. Try again.";
}

function photosOf(content: {
  heroPhoto?: string;
  logo?: string;
  gallery?: string[];
}): string[] {
  return [content.heroPhoto, content.logo, ...(content.gallery ?? [])].filter(
    (u): u is string => typeof u === "string" && u.length > 0,
  );
}

async function clientIp(): Promise<string | null> {
  const h = await headers();
  return h.get("x-forwarded-for")?.split(",")[0]?.trim() ?? null;
}

// ── Auth: email OTP (§1, §15) ────────────────────────────────────────────────

export async function sendOtp(input: { email: string }): Promise<ActionResult> {
  const parsed = z.object({ email: z.string().email() }).safeParse(input);
  if (!parsed.success) return { ok: false, error: "Enter a valid email address." };

  const ip = await clientIp();

  // A blocked account gets nothing — not a code, not an email, not a wasted send
  // (founder, 5 Aug 2026: "why waste OTP when email is blocked"). The auth ban is
  // still the real enforcement; this is the app knowing about it early enough to
  // say so at the first step instead of after he types a code that was correct.
  const blocked = await isEmailBlocked(parsed.data.email);
  if (blocked) {
    await logEvent({ action: "otp.blocked_account", meta: { email: parsed.data.email, ip } });
    return {
      ok: false,
      error: "This account has been blocked. Contact support if you think that is a mistake.",
    };
  }

  // The reviewer account has a password, not a code — nothing to send (lib/reviewer.ts).
  if (isReviewer(parsed.data.email)) {
    await logEvent({ action: "otp.reviewer", meta: { email: parsed.data.email, ip } });
    return { ok: true };
  }

  // Dev/test must never mail a real person — real codes are for real visitors.
  if (devMailBlocked(parsed.data.email))
    return {
      ok: false,
      error:
        "This is a development build; it only sends codes to allow-listed test addresses.",
    };

  // Throttle before spending a send: unthrottled, this endpoint lets anyone
  // pump codes at any inbox and burns our sending reputation with it.
  const { emailCount, ipCount } = await otpCounts(parsed.data.email, ip);
  const verdict = otpVerdict(emailCount, ipCount, parsed.data.email);
  if (!verdict.ok) {
    await logEvent({
      action: "otp.rate_limited",
      meta: { email: parsed.data.email, ip, reason: verdict.reason, emailCount, ipCount },
    });
    return { ok: false, error: verdict.message };
  }

  const supabase = await authClient();
  const { error } = await supabase.auth.signInWithOtp({
    email: parsed.data.email,
    options: { shouldCreateUser: true },
  });
  if (error) return { ok: false, error: "Couldn't send the code. Try again shortly." };

  // `ip` is recorded because the limiter counts these rows back (lib/rate-limit).
  await logEvent({ action: "otp.sent", meta: { email: parsed.data.email, ip } });
  return { ok: true };
}

export async function verifyOtp(input: {
  email: string;
  code: string;
}): Promise<ActionResult> {
  const parsed = z
    .object({ email: z.string().email(), code: z.string().min(4).max(10) })
    .safeParse(input);
  if (!parsed.success) return { ok: false, error: "Enter the code from your email." };

  const supabase = await authClient();
  // Reviewer: the "code" is the account password (lib/reviewer.ts).
  const { data, error } = isReviewer(parsed.data.email)
    ? await supabase.auth.signInWithPassword({
        email: parsed.data.email,
        password: parsed.data.code,
      })
    : await supabase.auth.verifyOtp({
        email: parsed.data.email,
        token: parsed.data.code,
        type: "email",
      });
  if (error || !data.user) {
    // a blocked account must be TOLD it is blocked. Saying "that code didn't work"
    // sends a banned customer round the loop forever, retyping a code that is right.
    const raw = (error?.message ?? "").toLowerCase();
    if (raw.includes("banned") || raw.includes("blocked")) {
      return { ok: false, error: "This account has been blocked. Contact support if you think that is a mistake." };
    }
    return { ok: false, error: "That code didn't work. Try again." };
  }

  await logEvent({ action: "login", userId: data.user.id });
  return { ok: true };
}

export async function signOut(): Promise<ActionResult> {
  const supabase = await authClient();
  await supabase.auth.signOut();
  return { ok: true };
}

// ── Sites & launches (§3) ────────────────────────────────────────────────────

/** Create a demo site from the create-flow form, guardrail it, and launch it. */
export async function createDraftAndLaunch(input: {
  templateKey: string;
  content: unknown;
}): Promise<ActionResult<{ slug: string; url: string; siteId: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please verify your email first." };

  const { data: tpl } = await admin()
    .from("templates")
    .select("id, category, enabled")
    .eq("key", input.templateKey)
    .maybeSingle();
  if (!tpl || !tpl.enabled) return { ok: false, error: "That template isn't available." };

  const schema = CONTENT_SCHEMAS[input.templateKey];
  if (!schema) return { ok: false, error: "That template isn't available." };
  const parsed = schema.safeParse(input.content);
  if (!parsed.success) return { ok: false, error: describeValidation(parsed.error) };
  const essentialError = essentialSectionError(
    input.templateKey,
    parsed.data as Record<string, unknown>,
  );
  if (essentialError) return { ok: false, error: essentialError };
  let content = parsed.data as { heroPhoto?: string; logo?: string; gallery?: string[] };
  try {
    content = await persistHeroPhoto(content, user.id);
    content = await persistLogo(content, user.id);
    content = await persistGallery(content, user.id);
  } catch (err) {
    return { ok: false, error: photoError(err) };
  }
  if (JSON.stringify(content).length > CONTENT_CAP_BYTES)
    return { ok: false, error: "That's a lot of content — please trim it a little." };

  // Slot check — recomputed from the DB, never the client (§3.1). ONE GATE for
  // every path that can create a demo site (template here, static upload in
  // createStaticSite): lib/orders.demoSlotRefusal, which knows a rep is not a
  // customer (Buds, 18 Aug 2026) and says what is actually full.
  const slotRefusal = await demoSlotRefusal(user.id);
  if (slotRefusal) return { ok: false, error: slotRefusal };

  // Throttle the paid guardrail call before running it.
  const saveThrottle = saveVerdict(await saveGuardrailCount(user.id));
  if (!saveThrottle.ok) return { ok: false, error: saveThrottle.message };

  // Guardrail — fail-closed (§6.2).
  const g = await guardrail({
    category: (tpl as { category: string }).category,
    content,
    photos: photosOf(content),
    userId: user.id,
  });
  if (!g.ok) return { ok: false, error: g.message };

  const { data: site, error: sErr } = await admin()
    .from("sites")
    .insert({
      user_id: user.id,
      template_id: (tpl as { id: string }).id,
      content,
      hero_photo: content.heroPhoto || null,
      status: "demo",
    })
    .select("id")
    .single();
  if (sErr || !site) return { ok: false, error: "Couldn't create your site. Try again." };
  await logEvent({
    action: "site.created",
    userId: user.id,
    siteId: site.id,
    meta: { template: input.templateKey },
  });

  const launched = await mintLaunch(site.id, user.id);
  if (!launched.ok) return launched;
  return { ok: true, data: { ...launched.data!, siteId: site.id } };
}

/** Relaunch an existing demo (mints a fresh 1-hour URL; §3.2). */
export async function launchDemo(
  siteId: string,
): Promise<ActionResult<{ slug: string; url: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const { data: site } = await admin()
    .from("sites")
    .select("id, status, user_id, origin, subdomain")
    .eq("id", siteId)
    .maybeSingle();
  if (!site || site.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (site.status !== "demo") return { ok: false, error: "Only demos can be launched." };

  // Relaunching while a launch is still live returns that SAME launch instead
  // of minting a fresh one (MMST-67). One demo site, one live link — this is
  // also what stops a scripted loop from growing `launches`/`demo.launched`
  // rows without bound: nothing on this path inserts or logs unless the
  // previous launch has actually expired.
  const { data: existing } = await admin()
    .from("launches")
    .select("slug")
    .eq("site_id", siteId)
    .gt("expires_at", new Date().toISOString())
    .order("expires_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (existing) return { ok: true, data: { slug: existing.slug, url: `https://${demoHost(existing.slug, APEX)}` } };

  return mintLaunch(
    siteId,
    user.id,
    true,
    site.origin === "static" ? (site.subdomain ?? undefined) : undefined,
  );
}

async function mintLaunch(
  siteId: string,
  userId: string,
  relaunch = false,
  fixedSlug?: string,
): Promise<ActionResult<{ slug: string; url: string }>> {
  const durationMin = await settings.demoDurationMin();
  // A static site's chosen name IS its address: the launch slug is fixed and
  // relaunching only refreshes the clock (upsert on slug). Template demos keep
  // their disposable random slugs (MMST-84).
  const slug = fixedSlug ?? newLaunchSlug();
  const expiresAt = new Date(Date.now() + durationMin * 60_000).toISOString();
  const { error } = fixedSlug
    ? await admin()
        .from("launches")
        .upsert(
          { site_id: siteId, slug, launched_at: new Date().toISOString(), expires_at: expiresAt },
          { onConflict: "slug" },
        )
    : await admin()
        .from("launches")
        .insert({ site_id: siteId, slug, expires_at: expiresAt });
  if (error) return { ok: false, error: "Couldn't launch the demo. Try again." };
  await logEvent({
    action: "demo.launched",
    userId,
    siteId,
    meta: { slug, expiresAt, relaunch },
  });
  return { ok: true, data: { slug, url: `https://${demoHost(slug, APEX)}` } };
}

// ─────────────────────────────────────────────────────────────────────────────
// WEBSITE FROM INVITATION CARDS (MMST-87): card photos in, website out. The
// user types NOTHING — AI reads the card and the result flows through the
// SAME createDraftAndLaunch as every template site: one slot gate, one
// guardrail, one photo pipeline, one launch mint.
// ─────────────────────────────────────────────────────────────────────────────

export async function createInvitationSite(input: {
  images: string[];
}): Promise<ActionResult<{ slug: string; url: string; siteId: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please verify your email first." };

  const images = (input.images ?? []).filter(
    (u) => typeof u === "string" && u.startsWith("data:image/"),
  );
  if (images.length === 0)
    return { ok: false, error: "Add at least one photo of the invitation card." };
  if (images.length > 5)
    return { ok: false, error: "Five card photos at most — front, inside and back is plenty." };

  // Refuse BEFORE the AI reads anything: a full slot must not cost a vision
  // call (economy), and the user hears the real reason first.
  const slotRefusal = await demoSlotRefusal(user.id);
  if (slotRefusal) return { ok: false, error: slotRefusal };

  const extracted = await extractInvitationContent(images);
  if (!extracted)
    return {
      ok: false,
      error:
        "Couldn't read that as an invitation card. Try clearer photos — good light, the whole card in frame.",
    };

  const content = {
    ...extracted,
    name: extracted.name.slice(0, 80),
    // The card photos themselves are the site's gallery — persisted to
    // storage by createDraftAndLaunch's photo pipeline.
    gallery: images,
  };

  return createDraftAndLaunch({ templateKey: "invitation-cards", content });
}

// ─────────────────────────────────────────────────────────────────────────────
// WEBSITE FROM BANNERS (MMST-89): banner photos in, event website out — the
// same shape as invitations: one slot gate before the AI call, one create
// path, one guardrail, one photo pipeline.
// ─────────────────────────────────────────────────────────────────────────────

export async function createBannerSite(input: {
  images: string[];
}): Promise<ActionResult<{ slug: string; url: string; siteId: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please verify your email first." };

  const images = (input.images ?? []).filter(
    (u) => typeof u === "string" && u.startsWith("data:image/"),
  );
  if (images.length === 0)
    return { ok: false, error: "Add at least one photo of the banner." };
  if (images.length > 5)
    return { ok: false, error: "Five banner photos at most." };

  const slotRefusal = await demoSlotRefusal(user.id);
  if (slotRefusal) return { ok: false, error: slotRefusal };

  const extracted = await extractBannerContent(images);
  if (!extracted)
    return {
      ok: false,
      error:
        "Couldn't read that as an event banner. Try a clearer photo — good light, the whole banner in frame.",
    };

  return createDraftAndLaunch({
    templateKey: "banner",
    content: { ...extracted, name: extracted.name.slice(0, 80), gallery: images },
  });
}

/** Re-read a banner site from its (possibly changed) photos — the only way
 *  its content changes. Mirrors updateInvitationSite. */
export async function updateBannerSite(input: {
  siteId: string;
  images: string[];
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };

  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, status, template:templates(key)")
    .eq("id", input.siteId)
    .maybeSingle();
  const s = site as unknown as {
    id: string; user_id: string; status: string;
    template: { key: string } | { key: string }[] | null;
  } | null;
  const tplKey = s ? (Array.isArray(s.template) ? s.template[0]?.key : s.template?.key) : null;
  if (!s || s.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (tplKey !== "banner")
    return { ok: false, error: "This site edits through its own form." };

  const images = (input.images ?? []).filter(
    (u) => typeof u === "string" && (u.startsWith("data:image/") || u.startsWith("http")),
  );
  if (images.length === 0)
    return { ok: false, error: "Keep at least one photo of the banner." };
  if (images.length > 5) return { ok: false, error: "Five banner photos at most." };

  const extracted = await extractBannerContent(images);
  if (!extracted)
    return {
      ok: false,
      error:
        "Couldn't read those as an event banner. Try clearer photos — good light, the whole banner in frame.",
    };

  return saveSiteContent({
    siteId: s.id,
    content: { ...extracted, name: extracted.name.slice(0, 80), gallery: images },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// STATIC UPLOADS (MMST-84, founder 23 Aug 2026): the second way a demo is
// born. The user brings his own single HTML file (made in an AI tool), picks
// a name, and gets A NORMAL DEMO — same slots, same launch, same
// bring-to-life, same payment. Not editable in the app: he edits the HTML
// outside and uploads the file again, as many times as he likes.
// ─────────────────────────────────────────────────────────────────────────────

/** Create a static-upload demo: one HTML file + a chosen subdomain name. */
/** Re-read an invitation site from its (possibly changed) card photos —
 *  the ONLY way its content changes (MMST-88): kept photos stay as their
 *  storage URLs, new ones arrive as data URLs; the card set is re-read whole
 *  and the site rebuilt through the same guardrailed save as every edit. */
export async function updateInvitationSite(input: {
  siteId: string;
  images: string[];
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };

  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, status, template:templates(key)")
    .eq("id", input.siteId)
    .maybeSingle();
  const s = site as unknown as {
    id: string; user_id: string; status: string;
    template: { key: string } | { key: string }[] | null;
  } | null;
  const tplKey = s ? (Array.isArray(s.template) ? s.template[0]?.key : s.template?.key) : null;
  if (!s || s.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (tplKey !== "invitation-cards")
    return { ok: false, error: "This site edits through its own form." };

  const images = (input.images ?? []).filter(
    (u) => typeof u === "string" && (u.startsWith("data:image/") || u.startsWith("http")),
  );
  if (images.length === 0)
    return { ok: false, error: "Keep at least one photo of the invitation card." };
  if (images.length > 5)
    return { ok: false, error: "Five card photos at most." };

  const extracted = await extractInvitationContent(images);
  if (!extracted)
    return {
      ok: false,
      error:
        "Couldn't read those as an invitation card. Try clearer photos — good light, the whole card in frame.",
    };

  // The same guardrailed save every template edit goes through: it persists
  // the new photos, checks content, and revalidates whatever is live.
  return saveSiteContent({
    siteId: s.id,
    content: { ...extracted, name: extracted.name.slice(0, 80), gallery: images },
  });
}

export async function createStaticSite(input: {
  subdomain: string;
  html: string;
}): Promise<ActionResult<{ slug: string; url: string; siteId: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please verify your email first." };

  const nameProblem = whyNotSubdomain(input.subdomain);
  if (nameProblem) return { ok: false, error: nameProblem };
  const subdomain = normaliseSubdomain(input.subdomain);

  const fileProblem = whyNotStaticHtml(input.html);
  if (fileProblem) return { ok: false, error: fileProblem };

  // Same gate as the template path — ONE slot rule (lib/orders.demoSlotRefusal).
  const slotRefusal = await demoSlotRefusal(user.id);
  if (slotRefusal) return { ok: false, error: slotRefusal };

  // The name must be free twice over: no site holds it, and no still-ticking
  // launch (template slugs are random 16-char names, but a clash would silently
  // re-point someone's URL — refuse instead).
  const { data: nameHolder } = await admin()
    .from("sites")
    .select("id")
    .eq("subdomain", subdomain)
    .maybeSingle();
  if (nameHolder) return { ok: false, error: "That name is taken — pick another." };
  const { data: slugHolder } = await admin()
    .from("launches")
    .select("site_id")
    .eq("slug", subdomain)
    .maybeSingle();
  if (slugHolder) return { ok: false, error: "That name is taken — pick another." };

  const { data: site, error: sErr } = await admin()
    .from("sites")
    .insert({
      user_id: user.id,
      template_id: null,
      origin: "static",
      subdomain,
      // name = the card's title; title = the document's own <title>, which the
      // live page serves as its metadata.
      content: { name: subdomain, title: extractHtmlTitle(input.html) },
      status: "demo",
      static_html_at: new Date().toISOString(),
    })
    .select("id")
    .single();
  if (sErr || !site)
    return { ok: false, error: "Couldn't create your site. Try again." };

  const { error: upErr } = await admin()
    .storage.from("static-sites")
    .upload(`${site.id}.html`, Buffer.from(input.html, "utf8"), {
      contentType: "text/html; charset=utf-8",
      upsert: true,
    });
  if (upErr) {
    // Never strand a slot on a failed upload: take the row back out.
    await admin().from("sites").delete().eq("id", site.id);
    return { ok: false, error: "Couldn't store the file. Try again." };
  }

  await logEvent({
    action: "site.created",
    userId: user.id,
    siteId: site.id,
    meta: {
      origin: "static",
      subdomain,
      bytes: Buffer.byteLength(input.html, "utf8"),
      title: extractHtmlTitle(input.html),
    },
  });

  const launched = await mintLaunch(site.id, user.id, false, subdomain);
  if (!launched.ok) return launched;
  return { ok: true, data: { ...launched.data!, siteId: site.id } };
}

/** Rename a static site's CARD TITLE — the address never changes (Buds,
 *  23 Aug 2026: "give the rename button ... just like other demos"). The
 *  template path renames through saveSiteContent + guardrail; a static site
 *  has no schema to parse, so the name gets its own small door. */
export async function renameStaticSite(input: {
  siteId: string;
  name: string;
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const name = input.name.trim();
  if (!name) return { ok: false, error: "Please enter a name." };
  if (name.length > 60) return { ok: false, error: "Keep the name to 60 characters or fewer." };

  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, origin, content")
    .eq("id", input.siteId)
    .maybeSingle();
  if (!site || site.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (site.origin !== "static")
    return { ok: false, error: "This site renames from its editor." };

  const content = { ...(site.content as Record<string, unknown>), name };
  const { error } = await admin()
    .from("sites")
    .update({ content, updated_at: new Date().toISOString() })
    .eq("id", site.id);
  if (error) return { ok: false, error: "Couldn't rename. Try again." };
  await logEvent({ action: "static.renamed", userId: user.id, siteId: site.id, meta: { name } });
  return { ok: true };
}

/** Replace a static site's HTML — the only way its content ever changes. */
export async function replaceStaticHtml(input: {
  siteId: string;
  html: string;
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };

  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, origin, subdomain, status, domain, content")
    .eq("id", input.siteId)
    .maybeSingle();
  if (!site || site.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (site.origin !== "static")
    return { ok: false, error: "This site is built from a template — use Edit instead." };
  if (!["demo", "live"].includes(site.status))
    return { ok: false, error: "This site isn't active." };

  const fileProblem = whyNotStaticHtml(input.html);
  if (fileProblem) return { ok: false, error: fileProblem };

  const { error: upErr } = await admin()
    .storage.from("static-sites")
    .upload(`${site.id}.html`, Buffer.from(input.html, "utf8"), {
      contentType: "text/html; charset=utf-8",
      upsert: true,
    });
  if (upErr) return { ok: false, error: "Couldn't store the file. Try again." };

  await admin()
    .from("sites")
    .update({
      static_html_at: new Date().toISOString(),
      // The new document brings its own <title>; the CARD TITLE the user chose
      // (renameStaticSite) survives a replace — only the file changed.
      content: { ...(site.content as Record<string, unknown>), title: extractHtmlTitle(input.html) },
      updated_at: new Date().toISOString(),
    })
    .eq("id", site.id);

  // Whatever is showing this site right now shows the new file next render.
  if (site.subdomain) revalidateLaunch(site.subdomain);
  if (site.status === "live") revalidateLive(site);

  await logEvent({
    action: "static.replaced",
    userId: user.id,
    siteId: site.id,
    meta: {
      bytes: Buffer.byteLength(input.html, "utf8"),
      title: extractHtmlTitle(input.html),
    },
  });
  return { ok: true };
}

/** Edit a site's content (demo or live). Guardrail + hash-skip + revalidate. */
export async function saveSiteContent(input: {
  siteId: string;
  content: unknown;
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };

  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, status, domain, subdomain, content, origin, template:templates(key, category)")
    .eq("id", input.siteId)
    .maybeSingle();
  if (!site || (site as { user_id: string }).user_id !== user.id)
    return { ok: false, error: "Site not found." };

  const s = site as unknown as {
    status: string;
    domain: string | null;
    subdomain: string | null;
    content: unknown;
    origin: string;
    template: { key: string; category: string } | null;
  };
  // A static upload has no editable content in the app — the ruling is that
  // he edits the HTML outside and uploads the file again (MMST-84).
  if (s.origin === "static")
    return {
      ok: false,
      error: "This site is a static upload — edit the HTML outside and upload the new file here.",
    };
  if (!s.template) return { ok: false, error: "Site not found." };

  const schema = CONTENT_SCHEMAS[s.template.key];
  if (!schema) return { ok: false, error: "This template can't be edited yet." };
  const parsed = schema.safeParse(input.content);
  if (!parsed.success) return { ok: false, error: describeValidation(parsed.error) };
  let content = parsed.data as { heroPhoto?: string; logo?: string; gallery?: string[] };
  try {
    content = await persistHeroPhoto(content, user.id);
    content = await persistLogo(content, user.id);
    content = await persistGallery(content, user.id);
  } catch (err) {
    return { ok: false, error: photoError(err) };
  }
  if (JSON.stringify(content).length > CONTENT_CAP_BYTES)
    return { ok: false, error: "That's a lot of content — please trim it a little." };

  // Hash-skip: unchanged content skips the AI call (relaunches/no-ops cost zero).
  const unchanged = JSON.stringify(content) === JSON.stringify(s.content);
  if (!unchanged) {
    // Throttle the paid guardrail call before running it.
    const saveThrottle = saveVerdict(await saveGuardrailCount(user.id));
    if (!saveThrottle.ok) return { ok: false, error: saveThrottle.message };

    const g = await guardrail({
      category: s.template.category,
      content,
      photos: photosOf(content),
      siteId: input.siteId,
      userId: user.id,
    });
    if (!g.ok) return { ok: false, error: g.message };
  }

  const { error } = await admin()
    .from("sites")
    .update({ content, hero_photo: content.heroPhoto || null, updated_at: new Date().toISOString() })
    .eq("id", input.siteId);
  if (error) return { ok: false, error: "Couldn't save. Try again." };

  await logEvent({ action: "site.saved", userId: user.id, siteId: input.siteId });

  // Revalidate any live surfaces (custom domain + active demo launches).
  if (s.status === "live") revalidateLive(s as { domain?: string | null; subdomain?: string | null });
  const { data: launches } = await admin()
    .from("launches")
    .select("slug")
    .eq("site_id", input.siteId)
    .gt("expires_at", new Date().toISOString());
  for (const l of (launches ?? []) as { slug: string }[]) revalidateLaunch(l.slug);

  return { ok: true };
}

/**
 * Delete a demo → frees the slot but KEEPS the row (the lead is immortal, §2.8).
 * Soft-deletes: status→'off', launches removed, hero photo cleared. Not for live
 * sites (those are managed via renewal/admin).
 */
export async function deleteSite(siteId: string): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const { data: site } = await admin()
    .from("sites")
    .select("id, user_id, status, origin, subdomain")
    .eq("id", siteId)
    .maybeSingle();
  if (!site || site.user_id !== user.id) return { ok: false, error: "Site not found." };
  if (site.status !== "demo") return { ok: false, error: "Only demos can be deleted here." };

  // ── Orders attached to this demo (founder, 5 Aug 2026: "what happens when
  // the customer deletes the demo?" — before this, nothing did: the order sat
  // in the admin's Needs-you queue forever and the domain stayed held for a
  // request the customer had already walked away from).
  const { data: orderRows } = await admin()
    .from("orders")
    .select("id, step, domain, order_code")
    .eq("site_id", siteId);
  const siteOrders = (orderRows as { id: string; step: string; domain: string | null; order_code: string | null }[]) ?? [];

  // Money in flight is not the customer's to erase with a delete button —
  // the admin resolves it (refund or live), and THEN the demo can go.
  //
  // awaiting_payment is NOT in flight (BUILD: MMST shop root): the rep raised
  // a basket and never collected on it, so nobody has paid and the office has
  // never seen it. Left out of this list it was treated as money in motion and
  // the demo could not be deleted at all — with a message about processing
  // that was simply untrue. It dies with the demo, exactly like 'created'.
  const inFlight = siteOrders.find(
    (o) => !["awaiting_payment", "created", "cancelled", "refunded"].includes(o.step),
  );
  if (inFlight) {
    return {
      ok: false,
      error:
        "An order on this site is already being processed. Contact support — once it is resolved you can delete the demo.",
    };
  }

  // An unanswered request dies with the demo: the order is cancelled, the
  // admin's queue clears itself, and the held name is released for the world.
  for (const o of siteOrders.filter((x) => ["awaiting_payment", "created"].includes(x.step))) {
    await admin()
      .from("orders")
      .update({ step: "cancelled", return_reason: "Customer deleted the demo.", updated_at: new Date().toISOString() })
      .eq("id", o.id);
    await cancelWorkForOrder(o.id, "the customer deleted the demo");
    if (o.domain) await releaseDomain(o.domain);
    await logEvent({
      action: "order.cancelled",
      userId: user.id,
      siteId,
      orderId: o.id,
      meta: { reason: "customer deleted the demo" },
    });
    // A cancellation leaves a paper trail: the customer gets it in writing.
    if (user.email) await sendOrderCancelled(user.email, { orderCode: o.order_code, domain: o.domain, siteId, userId: user.id });
  }

  await admin().from("launches").delete().eq("site_id", siteId);
  // A static site releases its NAME with the delete: sites are soft-deleted,
  // and an 'off' row squatting the unique subdomain would hold the address
  // forever. The uploaded file goes with it (MMST-84).
  if (site.origin === "static") {
    await admin().storage.from("static-sites").remove([`${siteId}.html`]);
  }
  const { error } = await admin()
    .from("sites")
    .update({
      status: "off",
      hero_photo: null,
      ...(site.origin === "static" ? { subdomain: null, static_html_at: null } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq("id", siteId);
  if (error) return { ok: false, error: "Couldn't delete. Try again." };

  await logEvent({
    action: "demo.deleted",
    userId: user.id,
    siteId,
    meta: site.origin === "static" ? { origin: "static", subdomain: site.subdomain } : undefined,
  });
  return { ok: true };
}

// ── Profile (MMST-20) — display name + avatar, distinct from KYC legal_name ──

export async function updateProfile(input: {
  displayName: string;
  avatarPhoto?: string;
}): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const parsed = z
    .object({ displayName: z.string().trim().max(80), avatarPhoto: z.string().optional() })
    .safeParse(input);
  if (!parsed.success) return { ok: false, error: describeValidation(parsed.error) };

  let record: Record<string, unknown> = { avatar: parsed.data.avatarPhoto ?? "" };
  try {
    record = await persistPhotoField(record, user.id, "avatar", 200_000);
  } catch (err) {
    return { ok: false, error: photoError(err) };
  }

  const { error } = await admin()
    .from("profiles")
    .upsert(
      {
        user_id: user.id,
        display_name: parsed.data.displayName || null,
        avatar_url: (record.avatar as string) || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" },
    );
  if (error) return { ok: false, error: "Couldn't save your profile. Try again." };
  await logEvent({ action: "profile.updated", userId: user.id });
  return { ok: true };
}

// ── Support (§13) — email-only ticket ────────────────────────────────────────

export async function createTicket(input: {
  subject: string;
  body: string;
  category?: string;
}): Promise<ActionResult<{ id: string; number: string }>> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const parsed = z
    .object({ subject: z.string().trim().min(2).max(140), body: z.string().trim().min(5).max(4000) })
    .safeParse(input);
  if (!parsed.success) return { ok: false, error: "Add a subject and a message." };
  const category = isCategory(input.category ?? "") ? input.category! : "other";

  // The number (MST-YYMM-NNNN) and the status are filled by the database
  // (0059 triggers) — this insert cannot get them wrong.
  const { data, error } = await admin()
    .from("tickets")
    .insert({
      user_id: user.id,
      email: user.email ?? "",
      subject: parsed.data.subject,
      body: parsed.data.body,
      category,
    })
    .select("id, number")
    .single();
  if (error || !data) return { ok: false, error: "Couldn't send. Try again." };
  const t = data as { id: string; number: string };
  await logEvent({ action: "ticket.created", userId: user.id, meta: { number: t.number, category } });
  return { ok: true, data: t };
}

/**
 * A ticket from someone with no session (founder, 5 Aug 2026). The blocked-account
 * message tells people to contact support, and until now there was nowhere to do
 * it: the only Contact link went to /dashboard/support, which bounces a logged-out
 * person to /login — and a blocked person can never get past /login. A support
 * door that only opens from inside is not a door.
 *
 * Lands in the SAME tickets table the admin already reads, with user_id null.
 */
export async function submitPublicContact(input: {
  email: string;
  subject: string;
  body: string;
  website?: string;
  stamp?: string;
}): Promise<ActionResult> {
  const parsed = z
    .object({
      email: z.string().email(),
      subject: z.string().min(2).max(140),
      body: z.string().min(5).max(4000),
      website: z.string().max(200).optional(),
      stamp: z.string().max(120).optional(),
    })
    .safeParse(input);
  if (!parsed.success) return { ok: false, error: "Fill in your email, a subject and a message." };

  // ── the bot checks. NOTHING here refuses the message. A false positive that
  // swallows a real customer's plea is worse than a spam row in a table nobody
  // has to look at, so a suspect message is stored, flagged, and kept out of the
  // queue instead (founder, 5 Aug 2026: "stop the spam coming to us").
  const reasons: string[] = [];
  if ((parsed.data.website ?? "").trim() !== "") reasons.push("honeypot");
  const verdict = checkFormStamp(parsed.data.stamp);
  if (verdict !== "ok" && verdict !== "stale") reasons.push(`stamp:${verdict}`);
  const links = (parsed.data.body.match(/https?:\/\//g) ?? []).length;
  if (links >= 3) reasons.push(`links:${links}`);
  const suspected = reasons.length > 0;

  const ip = await clientIp();
  // An open form on a public page is a spam target, but it must have its OWN
  // throttle: borrowing the login code-sender's counter meant a busy hour of
  // logins silently closed the support door (found while testing, 5 Aug 2026).
  const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
  const [{ count: mine }, { count: all }] = await Promise.all([
    admin()
      .from("tickets")
      .select("id", { count: "exact", head: true })
      .eq("email", parsed.data.email.toLowerCase())
      .gte("created_at", hourAgo),
    admin().from("tickets").select("id", { count: "exact", head: true }).gte("created_at", hourAgo),
  ]);
  const { count: fromIp } = ip
    ? await admin()
        .from("tickets")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", hourAgo)
    : { count: 0 };
  if ((fromIp ?? 0) >= 8) {
    // silent to the sender: telling a bot which wall it hit is free tuning for it
    await logEvent({ action: "contact.ip_throttled", meta: { ip } });
    return { ok: true };
  }
  if ((mine ?? 0) >= 5) {
    return { ok: false, error: "You have sent us several messages already. Give us an hour to reply." };
  }
  if ((all ?? 0) >= 200) {
    return { ok: false, error: "We are getting a lot of messages right now. Please try again shortly." };
  }

  // if this email HAS an account, attach the ticket to it so the admin sees the
  // person's whole history in one place
  const { data: existing } = await admin()
    .from("tickets")
    .select("user_id")
    .eq("email", parsed.data.email.toLowerCase())
    .not("user_id", "is", null)
    .limit(1)
    .maybeSingle();

  const { error } = await admin().from("tickets").insert({
    user_id: (existing as { user_id: string } | null)?.user_id ?? null,
    email: parsed.data.email.toLowerCase(),
    subject: parsed.data.subject,
    body: parsed.data.body,
    ip,
    suspected,
  });
  if (error) return { ok: false, error: "Couldn't send. Try again." };
  await logEvent({
    action: suspected ? "ticket.created_suspected" : "ticket.created_public",
    meta: { email: parsed.data.email, ip, reasons },
  });
  // a suspect sender is thanked exactly like anyone else — if it IS a person, he
  // has not been called a bot; if it is a bot, it learns nothing
  return { ok: true };
}

// ── The fed-up valve (§12.1) — link-based unsubscribe (account stays) ─────────

export async function unsubscribeLead(uid: string): Promise<ActionResult> {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uid))
    return { ok: false, error: "Invalid link." };
  const { error } = await admin()
    .from("profiles")
    .upsert(
      { user_id: uid, unsubscribed: true, updated_at: new Date().toISOString() },
      { onConflict: "user_id" },
    );
  if (error) return { ok: false, error: "Couldn't unsubscribe. Try again." };
  await logEvent({ action: "lead.unsubscribed", userId: uid });
  return { ok: true };
}

// ── Cancel an open order, keeping the demo (founder, 5 Aug 2026: "i want to
// keep the demo but want to cancel the order"). Only unpaid steps can be
// cancelled by the customer; money in flight is the admin's to resolve.
export async function cancelOrder(orderId: string): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const { data } = await admin()
    .from("orders")
    .select("id, user_id, step, domain, order_code, site_id")
    .eq("id", orderId)
    .maybeSingle();
  const o = data as { id: string; user_id: string; step: string; domain: string | null; order_code: string | null; site_id: string | null } | null;
  if (!o || o.user_id !== user.id) return { ok: false, error: "Order not found." };
  if (!["created", "returned"].includes(o.step))
    return { ok: false, error: "This order is already being processed. Contact support to stop it." };
  const { error } = await admin()
    .from("orders")
    .update({ step: "cancelled", return_reason: "Cancelled by the customer.", updated_at: new Date().toISOString() })
    .eq("id", o.id);
  if (!error) await cancelWorkForOrder(o.id, "the customer cancelled the order");
  if (error) return { ok: false, error: "Couldn't cancel. Try again." };
  if (o.domain) await releaseDomain(o.domain);
  await logEvent({ action: "order.cancelled", userId: user.id, siteId: o.site_id ?? undefined, orderId: o.id, meta: { reason: "cancelled by customer" } });
  if (user.email)
    await sendOrderCancelled(user.email, { orderCode: o.order_code, domain: o.domain, siteId: o.site_id ?? undefined, userId: user.id, reason: "by_request" });
  return { ok: true };
}

// ── Hide a cancelled order (MMST orders page) ────────────────────────────────
// "Delete" on the customer's Orders page. The row is NEVER deleted — the order
// is the record and the admin keeps full history — it only leaves HIS list.
// Only a cancelled order can go: anything with money or motion stays visible.
export async function hideOrder(orderId: string): Promise<ActionResult> {
  const user = await currentUser();
  if (!user) return { ok: false, error: "Please log in." };
  const { data } = await admin().from("orders").select("id, user_id, step").eq("id", orderId).maybeSingle();
  const o = data as { id: string; user_id: string; step: string } | null;
  if (!o || o.user_id !== user.id) return { ok: false, error: "Order not found." };
  if (o.step !== "cancelled") return { ok: false, error: "Only a cancelled order can be removed." };
  const { error } = await admin()
    .from("orders")
    .update({ hidden_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq("id", o.id);
  if (error) return { ok: false, error: "Couldn't remove it. Try again." };
  await logEvent({ action: "order.hidden", userId: user.id, orderId: o.id });
  return { ok: true };
}
